/** What every Templates chart says about its date range and its freshness
 *  (item 70): the answers the range leaves out, with one click to show them
 *  all, and when the answers shown were last written. */
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { activeLocale } from '@/utils/locale';
import type { InsightFilters } from '../../types/insights';
import { windowLabel } from '../reports/reportParts';
import { formatCount } from './templateParts';

/** "October 2026", "25 Sep – 24 Oct 2026": the range in force, as the page shows it. */
export function rangeLabel(filters: Pick<InsightFilters, 'dateFrom' | 'dateTo'>): string {
  return filters.dateFrom && filters.dateTo ? windowLabel({ start: filters.dateFrom, end: filters.dateTo }) : '';
}

/** "152 answers are outside this date range - Show all". Nothing when none are. */
export function OutsideRange({ outside, onShowAll, testId }: {
  outside: number; onShowAll: () => void; testId?: string;
}) {
  const { t } = useLanguage();
  if (!outside) return null;
  return (
    <p className="tpl-outside" role="status" data-testid={testId}>
      <CalendarOutlined aria-hidden />
      <span>{t('insights.tpl.range.outside', { count: outside, n: formatCount(outside) })}</span>
      <button type="button" className="tpl-link tpl-outside__all" onClick={onShowAll}>
        {t('insights.tpl.range.showAll')}
      </button>
    </p>
  );
}

/** "Updated 31 Oct, 18:22" in the viewer's own zone - when the answers a
    chart shows were last written. Nothing for a chart with none. */
export function UpdatedAt({ at }: { at?: string | null }) {
  const { t } = useLanguage();
  if (!at) return null;
  const when = new Intl.DateTimeFormat(activeLocale(), {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).format(new Date(at));
  return (
    <span className="tpl-updated" data-testid="template-updated">
      <ClockCircleOutlined aria-hidden />{t('insights.tpl.range.updated', { when })}
    </span>
  );
}
